import { useCallback, useMemo, useState } from 'react';
import { FCX, ID, Tooltip, useUuid } from '@datassential/platform-ui-lib';
import SocialLineChart, { SocialLineChartColumn } from '@components/SocialLineChart/SocialLineChart';
import { getAxisLabelString } from '@components/SocialLineChart/SocialLineChart.helpers';
import FoodTabs from '@pages/Food/components/Tabs/Tabs';
import { FoodSocialMetricEnum } from '@models/FoodSocial';
import { pctValueSignFormatter } from '@pages/Food/Food.helpers';
import { ChainSocialData } from '@apiHooks/useChainSocialData';
import './BrandPerceptionsSocialChart.scss';

interface BrandPerceptionsSocialChartProps {
    chainName: string;
    data: ChainSocialData;
    competitorData?: ChainSocialData | null;
    competitorName?: string;
    onMonthClick?: (column: SocialLineChartColumn | null) => void;
    selectedColumnId?: ID | null;
}

const CHART_COLORS = {
    primary: '#4FB0F9',
    competitor: '#FFFFFF',
};

const METRIC_TABS = [
    { id: FoodSocialMetricEnum.Mentions, title: FoodSocialMetricEnum.Mentions },
    { id: FoodSocialMetricEnum.Engagement, title: FoodSocialMetricEnum.Engagement },
];

const BrandPerceptionsSocialChart: FCX<BrandPerceptionsSocialChartProps> = ({
    chainName,
    data,
    competitorData,
    competitorName,
    onMonthClick,
    selectedColumnId = null,
}) => {
    const tabsTooltipId = useUuid();
    const hasData = data.hasData;

    const hasCompetitor = useMemo(
        () => !!competitorName && !!competitorData,
        [competitorName, competitorData]
    );
    const [activeTab, setActiveTab] = useState<FoodSocialMetricEnum>(FoodSocialMetricEnum.Mentions);

    const chartSeries = useMemo(() => {
        const values = activeTab === FoodSocialMetricEnum.Mentions ? data.chartData.mentions : data.chartData.engagement;

        const series = [
            {
                id: 'primary' as ID,
                title: chainName,
                color: CHART_COLORS.primary,
                values,
            },
        ];

        if (hasCompetitor && competitorData) {
            const competitorValues = activeTab === FoodSocialMetricEnum.Mentions
                ? competitorData.chartData.mentions
                : competitorData.chartData.engagement;

            series.push({
                id: 'competitor' as ID,
                title: competitorName!,
                color: CHART_COLORS.competitor,
                values: competitorValues,
            });
        }

        return series;
    }, [data, activeTab, chainName, hasCompetitor, competitorData, competitorName]);

    const growth = useMemo(
        () => activeTab === FoodSocialMetricEnum.Mentions ? data.growth.mentions : data.growth.engagement,
        [data.growth, activeTab]
    );

    const competitorGrowth = useMemo(
        () => {
            if (!competitorData) return null;
            return activeTab === FoodSocialMetricEnum.Mentions
                ? competitorData.growth.mentions
                : competitorData.growth.engagement;
        },
        [competitorData, activeTab]
    );

    const metricLabel = activeTab === FoodSocialMetricEnum.Mentions ? 'TOTAL MENTIONS' : 'TOTAL ENGAGEMENT';

    const selectedColumnIndex = useMemo(
        () => {
            if (selectedColumnId == null) return null;
            const idx = data.columns.findIndex(c => c.id === selectedColumnId);
            return idx !== -1 ? idx : null;
        },
        [selectedColumnId, data.columns]
    );

    const handleColumnClick = useCallback(
        (columnIndex: number) => {
            if (!onMonthClick) return;
            const column = data.columns[columnIndex];
            if (!column || !column.id) return;
            onMonthClick(column.id === selectedColumnId ? null : column);
        },
        [data.columns, selectedColumnId, onMonthClick]
    );

    return (
        <div className="BrandPerceptionsSocialChart" data-testid="blockBrandPerceptionsSocialChart">
                <div className="BrandPerceptionsSocialChart__chart-header HomeBrandPerceptions__panel-header">
                    <div className="BrandPerceptionsSocialChart__metric-label">
                        {metricLabel}
                        <span className="BrandPerceptionsSocialChart__metric-period">past 12 months</span>
                    </div>
                    <div data-tooltip-id={tabsTooltipId}>
                        <FoodTabs
                            data={METRIC_TABS}
                            activeTabId={activeTab}
                            onItemClick={(id) => setActiveTab(id as FoodSocialMetricEnum)}
                            className="BrandPerceptionsSocialChart__tabs"
                            testId="blockBrandPerceptionsTabs"
                        />
                    </div>
                    <Tooltip id={tabsTooltipId} place="bottom" style={{ zIndex: 20 }}>
                        <div style={{ width: 170 }}>
                            <div><b>Mentions</b></div>
                            <div>count of total mentions of this term</div>
                            <br/>
                            <div><b>Engagement</b></div>
                            <div>sum of likes, comment, and shares</div>
                        </div>
                    </Tooltip>
                </div>
                {hasData && (
                    <div className="BrandPerceptionsSocialChart__meta">
                        <div className="BrandPerceptionsSocialChart__growth">
                            <div className="BrandPerceptionsSocialChart__growth-item">
                                <span>Rolling 1-Month Growth</span>
                                <span className="BrandPerceptionsSocialChart__growth-badge is-primary">
                                    {pctValueSignFormatter(growth.past1Month ?? 0)}
                                </span>
                                {hasCompetitor && (
                                    <span className="BrandPerceptionsSocialChart__growth-badge is-competitor">
                                        {pctValueSignFormatter(competitorGrowth?.past1Month ?? 0)}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="BrandPerceptionsSocialChart__legend">
                            <div className="BrandPerceptionsSocialChart__legend-item">
                                <span className="BrandPerceptionsSocialChart__legend-dot" style={{ background: CHART_COLORS.primary }}/>
                                {chainName}
                            </div>
                            {hasCompetitor && (
                                <div className="BrandPerceptionsSocialChart__legend-item">
                                    <span className="BrandPerceptionsSocialChart__legend-dot" style={{ background: CHART_COLORS.competitor }}/>
                                    {competitorName}
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {!hasData && (
                    <div className="BrandPerceptionsSocialChart__meta">
                        <div className="BrandPerceptionsSocialChart__growth">
                            <div className="BrandPerceptionsSocialChart__growth-item">
                                <span>Rolling 1-Month Growth</span>
                            </div>
                        </div>
                    </div>
                )}
                <div className="BrandPerceptionsSocialChart__chart">
                    <SocialLineChart
                        series={hasData ? chartSeries : []}
                        columns={data.columns}
                        animationKey={`${activeTab}-${competitorName ?? 'none'}`}
                        renderValue={(v) => v !== null ? getAxisLabelString(v) : '-'}
                        renderTooltipTitle={(columnTitle) => (
                            <>
                                {metricLabel}<br/>
                                <span style={{ fontSize: 12, fontWeight: 400 }}>{columnTitle}</span>
                            </>
                        )}
                        emptyStateLabels={[4500, 3000, 1500, 0]}
                        onColumnClick={handleColumnClick}
                        selectedColumnIndex={selectedColumnIndex}
                        testId="blockBrandPerceptionsChart"
                    />
                </div>
        </div>
    );
};

export default BrandPerceptionsSocialChart;
